import {ChangeDetectionStrategy, Component, effect, Injectable, input, output, signal} from '@angular/core';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatButtonModule} from "@angular/material/button";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatTooltipModule} from "@angular/material/tooltip";
import {TranslateModule} from "@ngx-translate/core";
import {EnkelGruppeModell, GruppeId, HtmlTekst, ÅrshjulinnslagId, ÅrshjulInnslagModell, ÅrshjulinnslagSpesifikasjon, ÅrshjulRessursClient} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {MatIcon} from "@angular/material/icon";
import {KortHandlingerKomponent} from "web-ng2/delt/komponenter/kort/kort-handlinger/kort-handlinger";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";
import {håndterRedigerListeEndringer} from "felles-ng2/infrastruktur/angular/redigerListeSkjema";
import {Gruppedata} from "web-ng2/moduler/årshjul/komponenter/årshjul-graf/ÅrshjulGraf";
import {ÅrshjulModell} from "web-ng2/moduler/årshjul/komponenter/årshjul-graf/ÅrshjulModell";
import {Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {ÅrshjulGrafUtenMøterKomponent} from "web-ng2/moduler/årshjul/komponenter/årshjul-graf-uten-møter/årshjul-graf-uten-møter";
import {debounceTime} from "rxjs";

export type ÅrshjulInnslagSkjema = ReturnType<typeof ÅrshjulInnslagRedigerListeKomponent.opprettSkjemarad>
export type ÅrshjulInnslagSkjemaVerdier = ÅrshjulInnslagSkjema['value']

@Component({
    selector: 'aarshjul-innslag-rediger-liste',
    templateUrl: 'årshjul-innslag-rediger-liste.html',
    styleUrl: 'årshjul-innslag-rediger-liste.scss',
    imports: [
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatTooltipModule,
        TranslateModule,
        StandardValideringsfeilPipe,
        MatIcon,
        KortHandlingerKomponent,
        RiktekstRedigeringKomponent,
        ÅrshjulGrafUtenMøterKomponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ÅrshjulInnslagRedigerListeKomponent {

    /** Kalles når bruker har lagret eller avbryter, returnerer oppdaterte innslag ved lagret, null ved avbrutt */
    vedFerdig = output<ÅrshjulInnslagModell[] | null>();

    innslag = input.required<ÅrshjulInnslagModell[]>();
    gruppeId = input.required<GruppeId>();
    viserDiagram = input(false);

    lagrer = asynkArbeidPågårSignal();

    skjema = new FormArray<ÅrshjulInnslagSkjema>([]);
    aarshjuldata = signal<Gruppedata[]>([]);

    static opprettSkjemarad(verdier = {id: null as ÅrshjulinnslagId | null, tittel: '', beskrivelse: '' as HtmlTekst, startuke: 1, antallUker: 1, harForelder: false, ny: true}) {
        return new FormGroup({
            id: new FormControl<ÅrshjulinnslagId | null>(verdier.id),
            tittel: new FormControl<string>(verdier.tittel, {nonNullable: true, validators: FeltValidatorer.litenStreng}),
            beskrivelse: new FormControl<string>(verdier.beskrivelse, {nonNullable: true}),
            startuke: new FormControl<number>(verdier.startuke, {nonNullable: true, validators: [Validators.required, Validators.min(1), Validators.max(52)]}),
            antallUker: new FormControl<number>(verdier.antallUker, {nonNullable: true, validators: [Validators.required, Validators.min(1), Validators.max(52)]}),
            erArvet: new FormControl<boolean>(verdier.harForelder, {nonNullable: true}),
            slettet: new FormControl<boolean>(false, {nonNullable: true}),
            ny: new FormControl<boolean>(verdier.ny, {nonNullable: true})
        });
    }

    constructor(private avhengigheter: ÅrshjulInnslagRedigerListeKomponentAvhengigheter, private oversettelser: Oversettelser, private brukerkontekst: BrukerkontekstTilbyder) {
        this.skjema.valueChanges.pipe(takeUntilDestroyed())
          .subscribe(() => håndterRedigerListeEndringer(this.skjema, ÅrshjulInnslagRedigerListeKomponent.opprettSkjemarad));

        this.skjema.valueChanges.pipe(
          takeUntilDestroyed(),
          //ikke oppdater grafen ved hvert eneste tastetrykk
          debounceTime(200))
          .subscribe(() => this.populerGrafMedData());

        effect(() => {
            this.skjema.clear();
            this.innslag().forEach(innslag => {
                const rad = ÅrshjulInnslagRedigerListeKomponent.opprettSkjemarad({...innslag, ny: false});
                if (innslag.harForelder) rad.disable();
                this.skjema.push(rad, {emitEvent: false});
            });
            //sørger for at det blir lagt til en tom 'nytt innslag' rad
            håndterRedigerListeEndringer(this.skjema, ÅrshjulInnslagRedigerListeKomponent.opprettSkjemarad);
        });
    }

    async lagre() {
        const oppdaterteInnslag = await this.lagrer.utfør(() =>
          this.avhengigheter.oppdaterInnslag(this.gruppeId(), this.somSpesifikasjon()));
        this.vedFerdig.emit(oppdaterteInnslag);
    }

    avbryt() {
        this.vedFerdig.emit(null);
    }

    slett(indeks: number) {
        const rad = this.skjema.at(indeks);
        if (rad.value.ny) {
            this.skjema.removeAt(indeks);
        } else {
            rad.disable();
            rad.controls.slettet.setValue(true);
        }
    }

    angreSlett(indeks: number) {
        const rad = this.skjema.at(indeks);
        if (!rad.value.erArvet) rad.enable();
        rad.controls.slettet.setValue(false);
    }

    // private hjelpemetoder

    private async populerGrafMedData(): Promise<void> {
        const årshjulinnslag: ÅrshjulInnslagModell[] = this.somSpesifikasjon()
          //kun gyldige rader kan tas med (rader uten tittel har allerede blitt fjernet av somSpesifikasjon())
          .filter(rad => rad.startuke > 0 && rad.antallUker > 0)
          .map(s => ({...s, gruppeId: this.gruppeId()} as ÅrshjulInnslagModell))
        const modell = ÅrshjulModell.opprettFraÅrshjulinnslagOgMøter(årshjulinnslag, [], this.oversettelser);
        modell.setGrupper([this.gruppe()])
        this.aarshjuldata.set(await modell.dataForAlleÅrshjulRinger());
    }

    private gruppe(): EnkelGruppeModell {
        return this.brukerkontekst.autentisertBruker().finnAutentiserteGruppe(this.gruppeId())!;
    }

    private somSpesifikasjon(): ÅrshjulinnslagSpesifikasjon[] {
        return this.skjema.controls
          .filter(rad => !rad.value.slettet && !erTomEllerNil(rad.value.tittel))
          .map(rad => ({
              id: rad.value.id as unknown as ÅrshjulinnslagId,
              tittel: rad.value.tittel!,
              beskrivelse: rad.value.beskrivelse! as HtmlTekst,
              startuke: rad.value.startuke!,
              antallUker: rad.value.antallUker!
          }));
    }
}

@Injectable()
export class ÅrshjulInnslagRedigerListeKomponentAvhengigheter {

    constructor(private årshjulRessurs: ÅrshjulRessursClient) {
    }

    oppdaterInnslag(gruppeId: GruppeId, innslag: ÅrshjulinnslagSpesifikasjon[]): Promise<ÅrshjulInnslagModell[]> {
        return this.årshjulRessurs.oppdateralle(gruppeId, {innslag}).somPromise();
    }
}
